// /ops/finance — RankForge cost ledger. Variable spend (Anthropic,
// DataForSEO) tracked at API call sites; recurring monthly costs
// (Twilio, Ahrefs) operator-managed via `rankforge cost recurring-add`;
// domain renewals pulled live from CF Registrar API.

import { useQuery } from "@tanstack/react-query";
import { type FinanceResponse, type RenewalsResponse, opsApi } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";

const dollars = (cents: number): string =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function FinancePage() {
  const finance = useQuery({
    queryKey: ["ops-finance"],
    queryFn: opsApi.finance,
    refetchInterval: 60_000,
  });
  const renewals = useQuery({
    queryKey: ["ops-renewals"],
    queryFn: opsApi.renewals,
    refetchInterval: 5 * 60_000,
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-display font-bold">Finance</h1>
        <p className="text-ink-600 text-sm mt-1">
          Operator spend ledger + domain renewal calendar. Variable spend (Anthropic, DataForSEO) is auto-tracked at every API call;
          monthly recurring (Twilio, Ahrefs) added via{" "}
          <code className="bg-ink-100 px-1 rounded text-xs">rankforge cost recurring-add</code>.
        </p>
      </header>

      {finance.isLoading && <div className="text-ink-500 text-sm">Loading spend…</div>}
      {finance.error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm">
          Failed to load finance: {String((finance.error as Error).message)}
        </div>
      )}

      {finance.data && <SpendOverview data={finance.data} />}

      <RenewalsCard data={renewals.data} loading={renewals.isLoading} error={renewals.error as Error | null} />
    </div>
  );
}

function SpendOverview({ data }: { data: FinanceResponse }) {
  const mtdTotal = data.monthToDate.totalCents;
  const last30Total = data.last30Days.totalCents;
  const recurringMonthly = data.recurring.monthlyTotalCents;

  type ProviderRow = FinanceResponse["monthToDate"]["byProviderCategory"][number];
  const provider = useSortableTable<ProviderRow>(
    data.monthToDate.byProviderCategory,
    {
      provider: (r) => r.provider,
      category: (r) => r.category,
      events: (r) => r.events,
      spend: (r) => r.costCents,
    },
    { key: "spend", dir: "desc" },
  );

  type RecurringRow = FinanceResponse["recurring"]["items"][number];
  const recurring = useSortableTable<RecurringRow>(
    data.recurring.items,
    {
      provider: (r) => r.provider,
      description: (r) => r.description,
      monthly: (r) => r.monthlyCostCents,
      annual: (r) => r.monthlyCostCents * 12,
    },
    { key: "monthly", dir: "desc" },
  );

  return (
    <>
      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Month-to-date" value={dollars(mtdTotal)} hint={`${data.monthToDate.byProviderCategory.length} categories`} />
        <Tile label="Last 30 days" value={dollars(last30Total)} hint="variable spend" />
        <Tile label="Recurring / mo" value={dollars(recurringMonthly)} hint={`${data.recurring.items.length} active`} />
        <Tile label="Annual run-rate" value={dollars(data.annualRunRateCents)} hint="(last 30d + recurring) × 12" accent />
      </div>

      {/* By-provider this month */}
      <div className="bg-white rounded-xl border border-ink-100 p-5">
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-3">
          This month — by provider
        </h2>
        {data.monthToDate.byProviderCategory.length === 0 ? (
          <p className="text-sm text-ink-500">
            No spend recorded yet this month. Anthropic + DataForSEO instrumentation logs cost on every API call;
            the table fills up as the system runs.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink-500 border-b border-ink-100">
                <SortHeader label="Provider" sortKey="provider" currentSortKey={provider.sortKey} currentDir={provider.sortDir} onSort={provider.toggleSort} className="pb-2" />
                <SortHeader label="Category" sortKey="category" currentSortKey={provider.sortKey} currentDir={provider.sortDir} onSort={provider.toggleSort} className="pb-2" />
                <SortHeader label="Events" sortKey="events" currentSortKey={provider.sortKey} currentDir={provider.sortDir} onSort={provider.toggleSort} className="pb-2 text-right" />
                <SortHeader label="Spend" sortKey="spend" currentSortKey={provider.sortKey} currentDir={provider.sortDir} onSort={provider.toggleSort} className="pb-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {provider.sortedRows.map((row, i) => (
                <tr key={`${row.provider}-${row.category}-${i}`} className="border-b border-ink-50">
                  <td className="py-2 font-medium">{row.provider}</td>
                  <td className="py-2 text-xs text-ink-500 font-mono">{row.category ?? "—"}</td>
                  <td className="py-2 text-right tabular-nums text-ink-500">{row.events?.toLocaleString() ?? "—"}</td>
                  <td className="py-2 text-right tabular-nums font-medium">{dollars(row.costCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Daily timeline */}
      {data.dailySpend.length > 0 && (
        <div className="bg-white rounded-xl border border-ink-100 p-5">
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-3">
            Daily spend (last 30 days)
          </h2>
          <DailySparkline points={data.dailySpend} />
        </div>
      )}

      {/* Recurring */}
      <div className="bg-white rounded-xl border border-ink-100 p-5">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
            Recurring monthly
          </h2>
          <span className="text-xs text-ink-500">
            Add new: <code className="bg-ink-100 px-1 rounded">rankforge cost recurring-add</code>
          </span>
        </div>
        {data.recurring.items.length === 0 ? (
          <p className="text-sm text-ink-500">
            No recurring costs tracked. Add one with:<br />
            <code className="text-xs bg-ink-100 px-2 py-1 rounded inline-block mt-1">
              rankforge cost recurring-add --provider ahrefs --description "Standard tier" --monthly-usd 249
            </code>
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink-500 border-b border-ink-100">
                <SortHeader label="Provider" sortKey="provider" currentSortKey={recurring.sortKey} currentDir={recurring.sortDir} onSort={recurring.toggleSort} className="pb-2" />
                <SortHeader label="Description" sortKey="description" currentSortKey={recurring.sortKey} currentDir={recurring.sortDir} onSort={recurring.toggleSort} className="pb-2" />
                <SortHeader label="Monthly" sortKey="monthly" currentSortKey={recurring.sortKey} currentDir={recurring.sortDir} onSort={recurring.toggleSort} className="pb-2 text-right" />
                <SortHeader label="Annual" sortKey="annual" currentSortKey={recurring.sortKey} currentDir={recurring.sortDir} onSort={recurring.toggleSort} className="pb-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {recurring.sortedRows.map((r) => (
                <tr key={r.id} className="border-b border-ink-50">
                  <td className="py-2 font-medium">{r.provider}</td>
                  <td className="py-2 text-ink-600">{r.description}</td>
                  <td className="py-2 text-right tabular-nums">{dollars(r.monthlyCostCents)}</td>
                  <td className="py-2 text-right tabular-nums text-ink-500">{dollars(r.monthlyCostCents * 12)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function RenewalsCard({ data, loading, error }: { data: RenewalsResponse | undefined; loading: boolean; error: Error | null }) {
  type DomainRow = RenewalsResponse["domains"][number];
  const all = useSortableTable<DomainRow>(
    data?.domains ?? [],
    {
      domain: (d) => d.domain,
      expires: (d) => d.daysUntilExpiry,
      autoRenew: (d) => d.autoRenew == null ? null : d.autoRenew ? 1 : 0,
      status: (d) => d.status,
    },
  );
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5">
      <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700 mb-3">
        Domain renewal calendar
      </h2>
      {loading && <div className="text-ink-500 text-sm">Loading…</div>}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
          Failed: {String(error.message)}
        </div>
      )}
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-sm">
            <Tile label="Total domains" value={data.domains.length} />
            <Tile
              label="Renewing in 30d"
              value={data.nextRenewals30d.length}
              accent={data.nextRenewals30d.length > 0}
            />
            <Tile
              label="Auto-renew OFF"
              value={data.autoRenewOff.length}
              accent={data.autoRenewOff.length > 0}
              tone={data.autoRenewOff.length > 0 ? "red" : undefined}
            />
          </div>

          {data.nextRenewals30d.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs uppercase tracking-wider text-amber-800 font-medium mb-2">⚠ Renewing in next 30 days</h3>
              <table className="w-full text-sm">
                <tbody>
                  {data.nextRenewals30d.map((d) => (
                    <tr key={d.domain} className="border-b border-ink-50">
                      <td className="py-1.5 font-mono text-xs">{d.domain}</td>
                      <td className="py-1.5 text-xs text-ink-600">in {d.daysUntilExpiry}d</td>
                      <td className="py-1.5 text-xs">
                        {d.autoRenew === false ? (
                          <span className="text-red-700">⚠ auto-renew OFF</span>
                        ) : (
                          <span className="text-green-700">auto-renew on</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <details className="text-sm">
            <summary className="cursor-pointer text-ink-600 hover:text-ink-900">
              All {data.domains.length} domains
            </summary>
            <table className="w-full text-sm mt-2">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-ink-500 border-b border-ink-100">
                  <SortHeader label="Domain" sortKey="domain" currentSortKey={all.sortKey} currentDir={all.sortDir} onSort={all.toggleSort} className="pb-2" />
                  <SortHeader label="Expires" sortKey="expires" currentSortKey={all.sortKey} currentDir={all.sortDir} onSort={all.toggleSort} className="pb-2" />
                  <SortHeader label="Auto-renew" sortKey="autoRenew" currentSortKey={all.sortKey} currentDir={all.sortDir} onSort={all.toggleSort} className="pb-2" />
                  <SortHeader label="Status" sortKey="status" currentSortKey={all.sortKey} currentDir={all.sortDir} onSort={all.toggleSort} className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {all.sortedRows.map((d) => (
                  <tr key={d.domain} className="border-b border-ink-50">
                    <td className="py-1.5 font-mono text-xs">{d.domain}</td>
                    <td className="py-1.5 text-xs text-ink-500 tabular-nums">
                      {d.expiresAt ? new Date(d.expiresAt).toISOString().slice(0, 10) : "—"}
                      {d.daysUntilExpiry != null && (
                        <span className="text-ink-400 ml-1">({d.daysUntilExpiry}d)</span>
                      )}
                    </td>
                    <td className="py-1.5 text-xs">
                      {d.autoRenew == null ? "—" :
                        d.autoRenew ? <span className="text-green-700">on</span> : <span className="text-red-700">off</span>}
                    </td>
                    <td className="py-1.5 text-xs text-ink-500">{d.status ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </>
      )}
    </div>
  );
}

function DailySparkline({ points }: { points: { day: string; costCents: number }[] }) {
  // Inline SVG sparkline — same pattern as rank tracker. No chart lib.
  const W = 800;
  const H = 100;
  const PAD = 4;
  const max = Math.max(1, ...points.map((p) => p.costCents));
  const stepX = (W - PAD * 2) / Math.max(1, points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${(PAD + i * stepX).toFixed(1)} ${(H - PAD - (p.costCents / max) * (H - PAD * 2)).toFixed(1)}`)
    .join(" ");
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24">
        <path d={path} fill="none" stroke="rgb(14 165 233)" strokeWidth="2" />
        {points.map((p, i) => (
          <circle
            key={p.day}
            cx={PAD + i * stepX}
            cy={H - PAD - (p.costCents / max) * (H - PAD * 2)}
            r="2.5"
            fill="rgb(14 165 233)"
          >
            <title>{p.day}: {dollars(p.costCents)}</title>
          </circle>
        ))}
      </svg>
      <div className="flex justify-between text-xs text-ink-400 mt-1">
        <span>{points[0]?.day}</span>
        <span>peak: {dollars(max)}</span>
        <span>{points[points.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function Tile({
  label, value, hint, accent, tone,
}: { label: string; value: string | number; hint?: string; accent?: boolean; tone?: "red" }) {
  const cls = tone === "red"
    ? "bg-red-50 border-red-200 text-red-800"
    : accent
      ? "bg-accent-50 border-accent-200 text-accent-800"
      : "bg-ink-50 border-ink-200 text-ink-900";
  return (
    <div className={`border rounded-lg p-3 ${cls}`}>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-display font-bold tabular-nums mt-1">{value}</div>
      {hint && <div className="text-xs opacity-60 mt-0.5">{hint}</div>}
    </div>
  );
}
